import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { getConsent } from './CookieConsent';

const SNOOZE_KEY = 'uk_petition_support_snooze';
const ELAPSED_KEY = 'uk_petition_support_elapsed';
const BMC_URL = 'https://buymeacoffee.com/dostogir';

const DELAY_MS = 30 * 1000;              // total engaged time before asking
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // ask again after 3 days
const TICK_MS = 1000;                     // accumulator resolution

// ── Public helpers ────────────────────────────────────────────────────────────

const readNum = (key) => {
    try {
        const n = parseInt(localStorage.getItem(key), 10);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
        return 0;
    }
};

const writeNum = (key, n) => {
    try {
        localStorage.setItem(key, String(n));
    } catch {
        /* private mode — time simply doesn't carry across visits */
    }
};

// Snoozed until a future timestamp? Corrupt/absent values fall through to showing.
export const isSnoozed = () => {
    try {
        const until = parseInt(localStorage.getItem(SNOOZE_KEY), 10);
        return Number.isFinite(until) && Date.now() < until;
    } catch {
        return false;
    }
};

const snooze = () => {
    try {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
        // Restart the clock so the next ask needs another 30s of engagement.
        localStorage.removeItem(ELAPSED_KEY);
    } catch {
        /* private mode — modal simply reappears next visit */
    }
};

// ── Styles ────────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 1; }
`;

const scaleIn = keyframes`
    from { opacity: 0; transform: scale(0.92); }
    to   { opacity: 1; transform: scale(1); }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    z-index: 9998;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeIn} 0.25s ease;
`;

const Modal = styled.div`
    background: #ffffff;
    border-radius: 16px;
    max-width: 440px;
    width: 100%;
    padding: 36px 32px 28px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
    animation: ${scaleIn} 0.25s ease;
    text-align: center;
    position: relative;
`;

const CloseBtn = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: #bbb;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    border-radius: 8px;
    transition: color 0.15s, background 0.15s;

    &:hover { color: #555; background: #f4f4f4; }
`;

const IconWrap = styled.div`
    width: 56px;
    height: 56px;
    background: #FFDD00;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    font-size: 26px;
`;

const ModalTitle = styled.h2`
    font-size: 1.25em;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px;
`;

const ModalText = styled.p`
    font-size: 0.9em;
    color: #666;
    line-height: 1.6;
    margin: 0 0 8px;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #f0f0f0;
    margin: 20px 0;
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SupportLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    background: #FFDD00;
    color: #1a1a2e;
    border: none;
    border-radius: 10px;
    padding: 13px 20px;
    font-size: 0.95em;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover { opacity: 0.88; }
`;

const DismissBtn = styled.button`
    width: 100%;
    background: transparent;
    color: #888;
    border: 1.5px solid #e0e0e0;
    border-radius: 10px;
    padding: 11px 20px;
    font-size: 0.9em;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: color 0.2s, border-color 0.2s;

    &:hover {
        color: #444;
        border-color: #bbb;
    }
`;

const Note = styled.p`
    font-size: 0.78em;
    color: #aaa;
    margin: 14px 0 0;
    line-height: 1.5;
`;

// ── Component ─────────────────────────────────────────────────────────────────

const SupportModal = () => {
    const [visible, setVisible] = useState(false);

    // Accumulate total engaged time across page loads and visits, not just this
    // one. Only counts while the tab is actually visible, so a forgotten
    // background tab never reaches the threshold on its own.
    useEffect(() => {
        if (isSnoozed()) return;
        if (readNum(ELAPSED_KEY) >= DELAY_MS) {
            // Threshold already met on a previous visit — ask as soon as the
            // consent modal is out of the way.
            if (getConsent()) setVisible(true);
            return;
        }

        let elapsed = readNum(ELAPSED_KEY);
        let last = Date.now();

        const interval = setInterval(() => {
            const now = Date.now();
            const delta = now - last;
            last = now;

            if (document.visibilityState !== 'visible') return;

            // Ignore implausible jumps (device sleep, throttled background tab)
            // so suspended time is never counted as reading time.
            if (delta > 0 && delta < TICK_MS * 5) elapsed += delta;

            if (elapsed >= DELAY_MS) {
                writeNum(ELAPSED_KEY, DELAY_MS);
                clearInterval(interval);
                // Never stack on top of the cookie consent modal — if the
                // visitor hasn't chosen yet, stay quiet and ask next visit.
                if (getConsent()) setVisible(true);
            } else {
                writeNum(ELAPSED_KEY, elapsed);
            }
        }, TICK_MS);

        return () => clearInterval(interval);
    }, []);

    // Prevent background scroll while modal is open
    useEffect(() => {
        if (visible) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [visible]);

    const dismiss = useCallback(() => {
        snooze();
        setVisible(false);
    }, []);

    // Close on Escape, matching normal dialog behaviour
    useEffect(() => {
        if (!visible) return;
        const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [visible, dismiss]);

    if (!visible) return null;

    return (
        <Overlay onClick={dismiss}>
            <Modal
                role="dialog"
                aria-modal="true"
                aria-labelledby="support-title"
                onClick={(e) => e.stopPropagation()}
            >
                <CloseBtn onClick={dismiss} aria-label="Close">&times;</CloseBtn>

                <IconWrap>&#9749;</IconWrap>

                <ModalTitle id="support-title">Enjoying the tracker?</ModalTitle>

                <ModalText>
                    UK Petition Tracker is free, has no ads, and needs no account.
                    It's built and run by one person, and the servers that keep the
                    data flowing cost real money each month.
                </ModalText>

                <ModalText>
                    If it's been useful, a coffee helps keep it online.
                </ModalText>

                <Divider />

                <Buttons>
                    <SupportLink
                        href={BMC_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={dismiss}
                    >
                        &#9749; Support the site
                    </SupportLink>
                    <DismissBtn onClick={dismiss}>Maybe later</DismissBtn>
                </Buttons>

                <Note>
                    We won't ask again for a few days. You can always find the link
                    in the footer.
                </Note>
            </Modal>
        </Overlay>
    );
};

export default SupportModal;
